'use client'

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { rewardCodeApi } from '../api/endpoints'
import type { GiftPrize, RewardCode } from '../types'
import LuckyIcon from '../components/lucky/LuckyIcon'
import { showToast } from '../components/ui/Toast'
import { useLocale, useT } from '../contexts/LocaleContext'

type Step = 'enter' | 'pick' | 'done'
type CodeStatus = 'active' | 'used' | 'expired'

/** Zero / unset ValidTo (Go zero time) means the code never expires by calendar. */
function hasExpiry(validTo?: string): boolean {
  if (!validTo) return false
  const t = new Date(validTo).getTime()
  if (Number.isNaN(t)) return false
  if (t < Date.UTC(1970, 0, 2)) return false
  return true
}

function getCodeStatus(code: RewardCode): CodeStatus {
  if (code.is_used) return 'used'
  if (hasExpiry(code.valid_to) && new Date(code.valid_to).getTime() <= Date.now()) return 'expired'
  return 'active'
}

function statusLabel(
  status: CodeStatus,
  t: (key: string, vars?: Record<string, string | number>) => string,
  prize?: string,
) {
  if (status === 'used') {
    return prize ? t('gift.statusUsedWithPrize', { prize }) : t('gift.statusUsed')
  }
  if (status === 'expired') return t('gift.statusExpired')
  return t('gift.statusActiveHint')
}

function statusBadge(
  status: CodeStatus,
  t: (key: string, vars?: Record<string, string | number>) => string,
) {
  const label =
    status === 'used' ? t('gift.badgeUsed') : status === 'expired' ? t('gift.badgeExpired') : t('gift.badgeActive')
  return <span className={`lucky-gift__badge lucky-gift__badge--${status}`}>{label}</span>
}

export default function GiftCardPage() {
  const navigate = useNavigate()
  const t = useT()
  const { dir } = useLocale()
  const [step, setStep] = useState<Step>('enter')
  const [codeInput, setCodeInput] = useState('')
  const [activeCode, setActiveCode] = useState<RewardCode | null>(null)
  const [prizes, setPrizes] = useState<GiftPrize[]>([])
  const [selectedPrizeId, setSelectedPrizeId] = useState<number | null>(null)
  const [claimedPrize, setClaimedPrize] = useState<GiftPrize | null>(null)
  const [busy, setBusy] = useState(false)
  const [myCodes, setMyCodes] = useState<RewardCode[]>([])
  const [codeNotice, setCodeNotice] = useState<string | null>(null)

  useEffect(() => {
    rewardCodeApi
      .listMyCodes()
      .then((res) => setMyCodes(res.codes || []))
      .catch(() => setMyCodes([]))
  }, [])

  const { activeCodes, inactiveCodes } = useMemo(() => {
    const active: RewardCode[] = []
    const inactive: RewardCode[] = []
    for (const code of myCodes) {
      if (getCodeStatus(code) === 'active') active.push(code)
      else inactive.push(code)
    }
    return { activeCodes: active, inactiveCodes: inactive }
  }, [myCodes])

  const handleUnlock = async () => {
    const code = codeInput.trim()
    if (!code) {
      showToast(t('gift.errEnterCode'), 'error')
      return
    }
    setBusy(true)
    setCodeNotice(null)
    try {
      const res = await rewardCodeApi.unlock(code)
      setActiveCode(res.code)
      setPrizes(res.prizes || [])
      setSelectedPrizeId(null)
      setStep('pick')
    } catch (err: any) {
      const message = err.response?.data?.message || t('gift.errInvalidCode')
      if (message.includes('استفاده')) {
        setCodeNotice(t('gift.noticeAlreadyUsed'))
      } else if (message.includes('منقضی')) {
        setCodeNotice(t('gift.noticeExpired'))
      } else if (message.includes('یافت نشد')) {
        setCodeNotice(t('gift.noticeNotFound'))
      } else {
        setCodeNotice(message)
      }
      showToast(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleClaim = async () => {
    if (!activeCode || !selectedPrizeId) {
      showToast(t('gift.errPickPrize'), 'error')
      return
    }
    setBusy(true)
    try {
      const res = await rewardCodeApi.claim(activeCode.code, selectedPrizeId)
      setClaimedPrize(res.prize)
      setStep('done')
      showToast(res.message || t('gift.claimOk'), 'success')
      const list = await rewardCodeApi.listMyCodes()
      setMyCodes(list.codes || [])
    } catch (err: any) {
      showToast(err.response?.data?.message || t('gift.errClaim'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const resetFlow = () => {
    setStep('enter')
    setCodeInput('')
    setActiveCode(null)
    setPrizes([])
    setSelectedPrizeId(null)
    setClaimedPrize(null)
    setCodeNotice(null)
  }

  return (
    <div className="lucky-gift mini-app-container pb-28 min-h-screen">
      <div className="lucky-gift__inner">
        <header className="lucky-gift__header">
          <button type="button" className="lucky-gift__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-gift__header-icon">
            <LuckyIcon name="chest" size={32} alt="" />
          </div>
          <div>
            <h1>{t('gift.title')}</h1>
            <p>{t('gift.subtitle')}</p>
          </div>
        </header>

        {step === 'enter' && (
          <>
            <section className="lucky-gift__card">
              <p className="lucky-gift__intro">{t('gift.intro')}</p>
              <label className="lucky-gift__label" htmlFor="gift-code">
                {t('gift.codeLabel')}
              </label>
              <input
                id="gift-code"
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase())
                  setCodeNotice(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUnlock()
                }}
                placeholder={t('gift.codePlaceholder')}
                className="lucky-gift__input"
                dir="ltr"
              />
              {codeNotice && <div className="lucky-gift__notice">{codeNotice}</div>}
              <button
                type="button"
                className="lucky-gift__cta"
                onClick={handleUnlock}
                disabled={busy || !codeInput.trim()}
              >
                {busy ? '…' : t('gift.enterPrizes')}
              </button>
            </section>

            {activeCodes.length > 0 && (
              <section className="lucky-gift__card">
                <h2 className="lucky-gift__section-title">{t('gift.activeCodesTitle', { n: activeCodes.length })}</h2>
                <div className="lucky-gift__codes">
                  {activeCodes.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setCodeInput(item.code)
                        setCodeNotice(null)
                      }}
                      className="lucky-gift__code lucky-gift__code--active"
                      dir="ltr"
                    >
                      <div className="lucky-gift__code-top">
                        <span className="lucky-gift__mono">{item.code}</span>
                        {statusBadge('active', t)}
                      </div>
                      <span className="lucky-gift__code-sub">{statusLabel('active', t)}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {inactiveCodes.length > 0 && (
              <section className="lucky-gift__card">
                <h2 className="lucky-gift__section-title lucky-gift__section-title--muted">
                  {t('gift.inactiveCodesTitle', { n: inactiveCodes.length })}
                </h2>
                <div className="lucky-gift__codes">
                  {inactiveCodes.map((item) => {
                    const status = getCodeStatus(item)
                    return (
                      <div
                        key={item.id}
                        className={`lucky-gift__code lucky-gift__code--${status}`}
                        dir="ltr"
                      >
                        <div className="lucky-gift__code-top">
                          <span className="lucky-gift__mono">{item.code}</span>
                          {statusBadge(status, t)}
                        </div>
                        <span className="lucky-gift__code-sub">
                          {statusLabel(status, t, item.selected_prize)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {step === 'pick' && (
          <>
            <section className="lucky-gift__card">
              <p className="lucky-gift__label">{t('gift.activeCodeLabel')}</p>
              <p className="lucky-gift__mono lucky-gift__mono--lg" dir="ltr">
                {activeCode?.code}
              </p>
            </section>

            <h2 className="lucky-gift__pick-title">{t('gift.pickTitle')}</h2>
            <div className="lucky-gift__prizes">
              {prizes.map((prize) => {
                const selected = selectedPrizeId === prize.id
                return (
                  <button
                    key={prize.id}
                    type="button"
                    onClick={() => setSelectedPrizeId(prize.id)}
                    className={`lucky-gift__prize ${selected ? 'is-on' : ''}`}
                    aria-pressed={selected}
                  >
                    {prize.image_url ? (
                      <img src={prize.image_url} alt="" className="lucky-gift__prize-img" />
                    ) : (
                      <div className="lucky-gift__prize-fallback">
                        <LuckyIcon name="gift" size={24} alt="" />
                      </div>
                    )}
                    <div className="lucky-gift__prize-text">
                      <strong>{prize.title}</strong>
                      {prize.description && <span>{prize.description}</span>}
                    </div>
                    <em className={selected ? 'is-on' : ''} aria-hidden="true">
                      {selected ? '●' : '○'}
                    </em>
                  </button>
                )
              })}
            </div>

            <div className="lucky-gift__actions">
              <button type="button" className="lucky-gift__btn-sec" onClick={resetFlow} disabled={busy}>
                {t('common.back')}
              </button>
              <button
                type="button"
                className="lucky-gift__cta"
                onClick={handleClaim}
                disabled={busy || !selectedPrizeId}
              >
                {busy ? '…' : t('gift.claimPrize')}
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <section className="lucky-gift__card lucky-gift__done">
            <div className="lucky-gift__done-icon">
              <LuckyIcon name="confirm" size={48} alt="" />
            </div>
            <h2>{t('gift.doneTitle')}</h2>
            <p className="lucky-gift__done-title">{claimedPrize?.title}</p>
            {claimedPrize?.description && (
              <p className="lucky-gift__done-desc">{claimedPrize.description}</p>
            )}
            <p className="lucky-gift__done-note">{t('gift.doneNote')}</p>
            <button type="button" className="lucky-gift__cta" onClick={() => navigate('/')}>
              {t('gift.exit')}
            </button>
          </section>
        )}
      </div>
    </div>
  )
}
